import { WinbackPanel, applyOffer } from '../../../components/admin/billing/WinbackPanel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, Loader2, FileText, Download, Share2, Mail, Plus, Trash2,
  Copy, ChevronDown, ChevronRight, RotateCcw, Send, Truck, IndianRupee, ShieldCheck, Wand2,
} from 'lucide-react';
import ActionMenu from '../../../components/admin/billing/ActionMenu';
import RecordPaymentModal from '../../../components/admin/billing/RecordPaymentModal';
import InvoicePaymentLinkCard from './InvoicePaymentLinkCard';
import { InvoiceNotes } from '../../../components/admin/notes/InvoiceNotes';
import InvoiceWarrantyModal from '../../../components/admin/billing/InvoiceWarrantyModal';
import { PartPicker, PartChip, partLine, withPartQty } from '../../../components/admin/billing/PartPicker';
import { LineTotalInput } from '../../../components/admin/billing/LineInputs';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { greetName, sharePdfToWhatsApp, renderMessage } from '../../../lib/waShare';
import { sendPaymentThanks } from '../../../lib/paymentThanks';
import { useDeviceDraft } from '../../../lib/useDeviceDraft';
import { DraftBar } from '../../../components/DraftBar';
import {
  fmtINR, formatErr, notifyErr, inputCls, todayISO, AutoGrowTextarea,
} from '../../../components/admin/billing/shared';
import { useApiFeedback } from '../../../lib/useApiFeedback';

// Indian state codes (2-letter) — match non-GST form + backend
const STATES = [
  ['KA', 'Karnataka'], ['MH', 'Maharashtra'], ['DL', 'Delhi'], ['TN', 'Tamil Nadu'],
  ['KL', 'Kerala'], ['AP', 'Andhra Pradesh'], ['TS', 'Telangana'], ['GA', 'Goa'],
  ['GJ', 'Gujarat'], ['RJ', 'Rajasthan'], ['UP', 'Uttar Pradesh'], ['MP', 'Madhya Pradesh'],
  ['HR', 'Haryana'], ['PB', 'Punjab'], ['WB', 'West Bengal'], ['OD', 'Odisha'],
  ['BR', 'Bihar'], ['JH', 'Jharkhand'], ['CG', 'Chhattisgarh'], ['UK', 'Uttarakhand'],
  ['HP', 'Himachal Pradesh'], ['JK', 'Jammu & Kashmir'], ['AS', 'Assam'], ['CH', 'Chandigarh'],
  ['PY', 'Puducherry'],
];

const UNITS = ['NOS', 'SET', 'JOB', 'KGS', 'LTR', 'SQF', 'MTR'];

const STATE_NUM_TO_CODE = {
  '01': 'JK', '02': 'HP', '03': 'PB', '04': 'CH', '05': 'UK', '06': 'HR',
  '07': 'DL', '08': 'RJ', '09': 'UP', '10': 'BR', '11': 'SK', '12': 'AR',
  '13': 'NL', '14': 'MN', '15': 'MZ', '16': 'TR', '17': 'ML', '18': 'AS',
  '19': 'WB', '20': 'JH', '21': 'OD', '22': 'CG', '23': 'MP', '24': 'GJ',
  '27': 'MH', '28': 'AP', '29': 'KA', '30': 'GA', '32': 'KL', '33': 'TN',
  '34': 'PY', '36': 'TS',
};

const newRowId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
  `row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const REVENUE_HEADS = ['Detailing', 'Mechanical Servicing', 'Body Shop', 'Accessories', 'Consumables and Others'];

// Front-end mirror of backend classifier so the dropdown pre-selects the
// suggested head as the user types the description. First hit wins.
const HEAD_KEYWORDS = [
  ['Detailing', ['ceramic', 'coating', 'ppf', 'paint protection', 'sun film', 'sunfilm', 'tint', 'interior detail', 'polish', 'wax', 'buff']],
  ['Body Shop', ['repaint', 'paint job', 'denting', 'dent removal', 'panel beat', 'panel-beat', 'body work', 'insurance repair']],
  ['Mechanical Servicing', ['service', 'servicing', 'repair', 'brake', 'clutch', 'battery', 'oil change', 'coolant', 'suspension', 'engine', 'parts replacement', 'spare']],
  ['Accessories', ['accessory', 'accessories', 'mat', 'seat cover', 'mudflap', 'spoiler', 'roof carrier', 'chrome', 'led']],
];
const guessHead = (description) => {
  const t = (description || '').toLowerCase();
  for (const [head, kws] of HEAD_KEYWORDS) {
    if (kws.some((k) => t.includes(k))) return head;
  }
  return '';  // user explicitly picks Consumables and Others if nothing matches
};

const emptyItem = () => ({
  id: newRowId(),
  description: '', hsn_code: '', qty: 1, unit: 'NOS', rate: '',
  discount_pct: '', gst_rate: '', line_total_target: '',
  revenue_head: '',
});

const r2 = (v) => Math.round(v * 100) / 100;

// Mirrors backend _compute_taxes — including the tax-inclusive line-total
// back-computation used for round-figure billing.
const computeLine = (it, intra) => {
  const qty = Number(it.qty) || 0;
  const rate = Number(it.rate) || 0;
  const g = Number(it.gst_rate) || 0;
  const d = Number(it.discount_pct) || 0;
  const target = r2(Number(it.line_total_target) || 0);
  if (target > 0) {
    const taxExact = g ? (target * g) / (100 + g) : 0;
    let cgst = 0, sgst = 0, igst = 0;
    if (intra) { cgst = r2(taxExact / 2); sgst = cgst; } else { igst = r2(taxExact); }
    const taxable = r2(target - cgst - sgst - igst);
    const gross = d > 0 && d < 100 ? r2(taxable / (1 - d / 100)) : taxable;
    return { gross, dAmt: r2(gross - taxable), taxable, cgst, sgst, igst, total: target };
  }
  const gross = qty * rate;
  const dAmt = r2((gross * d) / 100);
  const taxable = r2(gross - dAmt);
  let cgst = 0, sgst = 0, igst = 0;
  if (intra) { cgst = r2((taxable * g) / 200); sgst = r2((taxable * g) / 200); } else { igst = r2((taxable * g) / 100); }
  return { gross, dAmt, taxable, cgst, sgst, igst, total: r2(taxable + cgst + sgst + igst) };
};

// Fields that can override Billing Settings on this document only.
const OVERRIDE_FIELDS = [
  'bank_holder_name', 'bank_name', 'bank_account_number', 'bank_ifsc', 'upi',
  'terms_pay', 'terms_service',
];

const emptyForm = () => ({
  customer_id: '',
  customer_snapshot: { name: '', gstin: '', state: '', address: '', phone: '', email: '' },
  vehicle_snapshot: null,
  place_of_supply: 'KA',
  invoice_date: todayISO(),
  due_date: '',
  reverse_charge: false,
  subject: '',
  notes: '',
  items: [emptyItem()],
  eway_bill: { ewb_no: '', ewb_date: '', transporter_id: '', transporter_name: '', vehicle_no: '', distance_km: 0 },
  status: 'draft',
  amount_paid: 0,
  advance_received: 0,
  // Overrides (null = inherit)
  bank_holder_name: null, bank_name: null, bank_account_number: null,
  bank_ifsc: null, upi: null, terms_pay: null, terms_service: null,
  // Payroll incentive credit (mirrors non-GST invoice)
  worked_on_by_ids: [],
  incentive_per_staff_override: '',
});

const linesToArray = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean);
const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

const GstInvoiceForm = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const Title = 'GST Invoice';

  const [form, setForm] = useState(emptyForm());
  const [serverInv, setServerInv] = useState(null);
  const [hsnList, setHsnList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null); // for override defaults
  const [gstSettings, setGstSettings] = useState(null); // for supplier state code
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showEwb, setShowEwb] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const { error: err, showError, showSuccess, clearError, bannerRef: errBannerRef } = useApiFeedback();

  const fetchSupporting = useCallback(async () => {
    try {
      const [h, gs, c, bs, staff] = await Promise.all([
        axios.get(`${API}/gst/hsn`, { headers: authHeader() }),
        axios.get(`${API}/gst/settings`, { headers: authHeader() }),
        axios.get(`${API}/billing/customers`, { headers: authHeader() }),
        axios.get(`${API}/billing/settings`, { headers: authHeader() }),
        axios.get(`${API}/staff`, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setHsnList(h.data || []);
      setGstSettings(gs.data);
      setCustomers(c.data || []);
      setBillingSettings(bs.data);
      setStaffList((staff.data || []).filter((s) => s.status === 'active'));
    } catch (e) { /* non-fatal — save call will surface */ }
  }, [authHeader]);

  const loadInvoice = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gst/invoices/${id}`, { headers: authHeader() });
      setForm({
        winback_code: data.winback_code || '',
        customer_id: data.customer_id || '',
        customer_snapshot: data.customer_snapshot || { name: '', gstin: '', state: '' },
        vehicle_snapshot: (data.vehicle_snapshot && Object.keys(data.vehicle_snapshot).length) ? data.vehicle_snapshot : null,
        place_of_supply: data.place_of_supply || 'KA',
        invoice_date: data.invoice_date || todayISO(),
        due_date: data.due_date || '',
        reverse_charge: !!data.reverse_charge,
        subject: data.subject || '',
        notes: data.notes || '',
        items: (data.items || []).map((it) => ({ ...it, id: it.id || newRowId(), line_total_target: it.line_total_target ?? '' })),
        eway_bill: data.eway_bill || { ewb_no: '', vehicle_no: '', transporter_id: '', transporter_name: '', distance_km: 0, ewb_date: '' },
        status: data.status || 'draft',
        amount_paid: data.amount_paid || 0,
        advance_received: data.advance_received || 0,
        bank_holder_name: data.bank_holder_name ?? null,
        bank_name: data.bank_name ?? null,
        bank_account_number: data.bank_account_number ?? null,
        bank_ifsc: data.bank_ifsc ?? null,
        upi: data.upi ?? null,
        terms_pay: data.terms_pay ?? null,
        terms_service: data.terms_service ?? null,
        worked_on_by_ids: data.worked_on_by_ids || [],
        incentive_per_staff_override: data.incentive_per_staff_override ?? '',
      });
      if (data.eway_bill?.ewb_no || data.eway_bill?.vehicle_no) setShowEwb(true);
      if (OVERRIDE_FIELDS.some((f) => data[f] !== undefined && data[f] !== null)) {
        setShowOverrides(true);
      }
      setServerInv(data);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  }, [authHeader, id, isNew, showError]);

  useEffect(() => { fetchSupporting(); }, [fetchSupporting]);
  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  const draft = useDeviceDraft(`gst:${id || 'new'}`, form, setForm, isNew || (!loading && !!serverInv));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const applyWinback = (offer) => {
    if (offer === null) { setForm((s) => ({ ...s, winback_code: '' })); return; }
    const { items, discount } = applyOffer(form.items, offer, { gst: true, makeItem: emptyItem });
    if (!discount) { toast.error('Add items first, then apply the code'); return; }
    setForm((s) => ({ ...s, items, winback_code: offer.code }));
    toast.success(`${offer.code} applied: ₹${discount.toLocaleString('en-IN')} off`);
  };
  const setSnap = (patch) => setForm((s) => ({ ...s, customer_snapshot: { ...s.customer_snapshot, ...patch } }));

  /**
   * Detach the invoice from the linked customer-master row so subsequent
   * edits to the Bill-To fields stay put and the picker stops "snapping
   * back" to the original customer. Also invoked automatically the
   * moment the operator manually changes any of the Bill-To identity
   * fields (name / phone / gstin) — mirrors how draft invoices behave
   * in most billing tools.
   */
  const detachCustomer = () => {
    setForm((s) => ({ ...s, customer_id: '', vehicle_snapshot: null }));
  };
  const setSnapDetached = (patch) => {
    setForm((s) => ({
      ...s,
      // If the operator is changing an identity field on an
      // already-linked invoice, silently detach so the picker no
      // longer overrides the edit on save.
      customer_id: '',
      customer_snapshot: { ...s.customer_snapshot, ...patch },
    }));
  };
  const setItem = (idx, patch) => setForm((f) => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...withPartQty(it, patch) } : it),
  }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const addPart = (part) => setForm((f) => {
    const line = { ...emptyItem(), ...partLine(part, { taxed: true, split: false }) };
    const items = f.items.slice();
    const last = items[items.length - 1];
    if (last && !last.description && !Number(last.rate)) items[items.length - 1] = line;
    else items.push(line);
    return { ...f, items };
  });
  const delRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickHsn = (idx, code) => {
    const found = hsnList.find((h) => h.code === code);
    if (found) setItem(idx, { hsn_code: found.code, gst_rate: found.default_gst_rate });
    else setItem(idx, { hsn_code: code });
  };

  const tryAutoMatchHsn = async (idx) => {
    const it = form.items[idx];
    if (!it || !it.description || it.hsn_code) return;
    try {
      const { data } = await axios.get(`${API}/gst/hsn/match?name=${encodeURIComponent(it.description)}`, { headers: authHeader() });
      if (data && data.hsn_code) {
        setItem(idx, { hsn_code: data.hsn_code, gst_rate: data.gst_rate || it.gst_rate });
      }
    } catch (_) { /* best-effort */ }
  };

  const vehSnapFrom = (v) => ({
    id: v.id, make: v.make || '', model: v.model || '', year: v.year || '',
    color: v.color || '', registration_number: v.registration_number || '',
  });
  const vehLabel = (v) => {
    const base = [v.year, v.make, v.model].filter(Boolean).join(' ');
    return v.registration_number ? `${base}${base ? ' · ' : ''}${v.registration_number}` : base;
  };

  const pickCustomer = (cid) => {
    if (!cid) { set('customer_id', ''); return; }
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    const vehicles = c.vehicles || [];
    setForm((f) => ({
      ...f, customer_id: cid, winback_code: f.customer_id === cid ? f.winback_code : '',
      customer_snapshot: {
        id: c.id, name: c.name, phone: c.phone || '', email: c.email || '',
        address: c.address || '', gstin: c.gstin || '', state: c.state || '',
      },
      vehicle_snapshot: vehicles.length === 1 ? vehSnapFrom(vehicles[0]) : null,
      place_of_supply: c.state && STATES.find((s) => s[0] === c.state) ? c.state : f.place_of_supply,
    }));
  };

  const vehicleOptions = useMemo(
    () => (customers.find((c) => c.id === form.customer_id)?.vehicles) || [],
    [customers, form.customer_id],
  );

  const pickVehicle = (vid) => {
    if (!vid) { set('vehicle_snapshot', null); return; }
    const v = vehicleOptions.find((x) => x.id === vid);
    if (v) set('vehicle_snapshot', vehSnapFrom(v));
  };

  // Live client-side tax preview — server is authoritative on save.
  const preview = useMemo(() => {
    const supplierCode = STATE_NUM_TO_CODE[gstSettings?.supplier_state_code] || 'KA';
    const intra = (form.place_of_supply || '').toUpperCase() === supplierCode;
    let sub = 0, disc = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
    (form.items || []).forEach((it) => {
      const L = computeLine(it, intra);
      sub += L.gross; disc += L.dAmt; taxable += L.taxable;
      cgst += L.cgst; sgst += L.sgst; igst += L.igst;
    });
    const r2v = r2;
    return {
      intra, sub_total: r2v(sub), total_discount: r2v(disc), taxable_value: r2v(taxable),
      cgst_total: r2v(cgst), sgst_total: r2v(sgst), igst_total: r2v(igst),
      total_tax: r2v(cgst + sgst + igst),
      grand_total: r2v(taxable + cgst + sgst + igst),
    };
  }, [form.items, form.place_of_supply, gstSettings]);

  const save = async () => {
    if (!form.customer_snapshot.name?.trim()) { showError('Customer name required.'); return; }
    if (!form.items.length) { showError('At least one line item required.'); return; }
    clearError(); setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          ...it,
          qty: Number(it.qty) || 0,
          rate: Number(it.rate) || 0,
          discount_pct: Number(it.discount_pct) || 0,
          gst_rate: Number(it.gst_rate) || 0,
          // Auto-suggest head if the user left the dropdown blank — same
          // classifier the backend uses so blank-heads bucket consistently.
          revenue_head: (it.revenue_head && it.revenue_head.trim()) || guessHead(it.description) || '',
          line_total_target: it.line_total_target !== '' && it.line_total_target != null
            ? Number(it.line_total_target)
            : null,
        })),
        vehicle_snapshot: form.vehicle_snapshot || {},
        advance_received: Number(form.advance_received || 0),
        incentive_per_staff_override: form.incentive_per_staff_override !== '' && form.incentive_per_staff_override != null
          ? Number(form.incentive_per_staff_override)
          : null,
      };
      // Only include overrides the user has explicitly set (null = inherit).
      OVERRIDE_FIELDS.forEach((f) => { if (payload[f] === null || payload[f] === undefined) delete payload[f]; });

      let saved;
      if (isNew) {
        ({ data: saved } = await axios.post(`${API}/gst/invoices`, payload, { headers: authHeader() }));
        draft.clear();
        navigate(`/admin/gst/invoices/${saved.id}`, { replace: true });
      } else {
        ({ data: saved } = await axios.put(`${API}/gst/invoices/${id}`, payload, { headers: authHeader() }));
        draft.clear();
        setServerInv(saved);
      }
      showSuccess(`${Title} saved.`);
      (saved?.stock_warnings || []).forEach((w) => toast.warning(`Running low: ${w}`));
    } catch (e) { showError(e); }
    finally { setSaving(false); }
  };

  const pdfHref = !isNew ? `${API}/gst/invoices/${id}/pdf` : '#';

  const downloadPdf = async () => {
    if (isNew) { alert('Please save the invoice first.'); return; }
    try {
      const resp = await axios.get(`${API}/gst/invoices/${id}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `GST_${(serverInv?.number || id).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (e) { notifyErr(e); }
  };

  const shareWhatsApp = async () => {
    if (isNew) { alert('Please save the invoice first.'); return; }
    const c = form.customer_snapshot || {};
    const phone = (c.phone || '').replace(/\D/g, '');
    const total = Number(serverInv?.net_total ?? preview.grand_total ?? 0);
    const paid = Number(serverInv?.amount_paid || 0) - Number(serverInv?.notes_refund_total || 0) - Number(serverInv?.notes_credit_carried || 0);
    const advance = Number(form.advance_received || 0);
    // Balance due nets out both the upfront advance (before invoice) and
    // any post-invoice payments logged via RecordPaymentModal.
    const balance = Math.max(0, total - paid - advance);
    const status = serverInv?.status || '';
    const subjectClause = form.subject ? ` for ${form.subject}` : '';
    const number = serverInv?.number || id;

    // Mint a public token-protected PDF URL so the customer can open the
    // invoice on WhatsApp without an admin login. Falls back to a plain
    // deep-link if the mint fails so ops can still push a message.
    let pdfUrl = `${window.location.origin}/api/gst/invoices/${id}/pdf`;
    try {
      const { data } = await axios.post(`${API}/gst/invoices/${id}/share-link`, {}, { headers: authHeader() });
      if (data?.pdf_url) pdfUrl = data.pdf_url.startsWith('http') ? data.pdf_url : `${window.location.origin}${data.pdf_url}`;
    } catch (_e) { /* silent — fall back to authed URL */ }

    const cashfreeUrl = serverInv?.cashfree_link_url;
    const cashfreeActive = cashfreeUrl && ['ACTIVE', 'PARTIALLY_PAID', ''].includes(String(serverInv?.cashfree_payment_status || '').toUpperCase()) && balance > 0;

    const key = status === 'paid' ? 'invoice_paid'
      : status === 'overdue' ? 'invoice_overdue'
        : (paid > 0 || advance > 0) ? 'invoice_partial' : 'invoice_unpaid';
    const message = renderMessage(key, {
      customer: greetName(c.prefix, c.name), number, doc_type: 'GST tax invoice', for_subject: subjectClause,
      total: fmtINR(total), gst_note: ' (all-inclusive of GST)', advance: advance > 0 ? fmtINR(advance) : '',
      paid: paid > 0 ? fmtINR(paid) : '', balance: fmtINR(balance), pdf_link: pdfUrl || '',
      pay_link: cashfreeActive ? cashfreeUrl : '', upi: '',
    });

    // Preferred path — native share sheet on iOS / Android / Chromium so
    // the user can pick WhatsApp and the PDF gets ATTACHED as a file
    // (not just a link). Falls back to the classic wa.me deep-link on
    // browsers that don't support file-sharing (older desktops).
    await sharePdfToWhatsApp({
      getPdf: async () => (await axios.get(`${API}/gst/invoices/${id}/pdf`, { responseType: 'blob', headers: authHeader() })).data,
      filename: `GST_${String(number).replace(/\//g, '_')}.pdf`,
      title: `GST Invoice ${number}`, message, phone,
    });
  };

  const emailToCustomer = async () => {
    if (isNew) { alert('Please save the invoice first.'); return; }
    const to = form.customer_snapshot?.email;
    if (!to) { alert('Customer has no email. Add one on the customer profile first.'); return; }
    if (!window.confirm(`Email this GST invoice to ${to}?`)) return;
    setEmailing(true);
    try {
      const { data } = await axios.post(`${API}/gst/invoices/${id}/email`, {}, { headers: authHeader() });
      alert(`GST invoice emailed to ${data.sent_to}.`);
      loadInvoice();
    } catch (e) { notifyErr(e); }
    finally { setEmailing(false); }
  };

  const markSent = async () => {
    if (isNew) return;
    if (!window.confirm('Mark this GST invoice as Issued? Use this when you share via WhatsApp or hand over a printout.')) return;
    setMarking(true);
    try {
      await axios.post(`${API}/gst/invoices/${id}/mark-sent`, {}, { headers: authHeader() });
      loadInvoice();
    } catch (e) { notifyErr(e); }
    finally { setMarking(false); }
  };

  const cloneInvoice = async () => {
    if (isNew) return;
    if (!window.confirm('Clone this GST invoice as a new draft with a fresh number? Payments / status reset.')) return;
    setCloning(true);
    try {
      const { data } = await axios.post(`${API}/gst/invoices/${id}/clone`, {}, { headers: authHeader() });
      navigate(`/admin/gst/invoices/${data.id}`);
    } catch (e) { notifyErr(e); }
    finally { setCloning(false); }
  };

  // Override helpers — mirror BillingForm exactly so behaviour is identical.
  const settingsFallback = (k) => (billingSettings ? (billingSettings[k] ?? '') : '');
  const effective = (k) => (form[k] !== null && form[k] !== undefined ? form[k] : settingsFallback(k));
  const setOverride = (k, v) => set(k, v);
  const resetOverride = (k) => {
    if (!window.confirm(`Reset "${k}" to Billing Settings default?`)) return;
    set(k, null);
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" />Loading…</div>;
  }

  return (
    <div className="min-h-screen" data-testid="gst-invoice-form" onInputCapture={draft.touch} onChangeCapture={draft.touch} onClickCapture={draft.touch}>
      <DraftBar draft={draft} />
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">GST Tax Invoice</div>
            <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate text-slate-900" data-testid="form-title">
              {isNew ? 'New GST Invoice' : (serverInv?.number || id)}
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {isNew ? 'Create a new GST tax invoice' : `Customer: ${form.customer_snapshot?.name || '—'} · Status: ${form.status}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {!isNew && (
              <>
                <a href={pdfHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 sm:px-3 py-2 rounded-lg" data-testid="gst-view-pdf-btn">
                  <FileText size={14} /> <span className="hidden sm:inline">View </span>PDF
                </a>
                <button onClick={shareWhatsApp} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 text-emerald-700 px-2.5 sm:px-3 py-2 rounded-lg" data-testid="gst-share-whatsapp-btn">
                  <Share2 size={14} /> <span className="hidden sm:inline">WhatsApp</span>
                </button>
                <button
                  onClick={() => setShowPayModal(true)}
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-100 hover:bg-teal-200 border border-teal-300 text-teal-700 px-2.5 sm:px-3 py-2 rounded-lg"
                  data-testid="gst-record-payment-btn"
                  title="Record a payment against this invoice"
                >
                  <IndianRupee size={14} />
                  <span className="hidden sm:inline">Record Payment</span>
                </button>
                {serverInv?.linked_warranty_id ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-violet-100 border border-violet-300 text-violet-700 px-2.5 sm:px-3 py-2 rounded-lg font-mono"
                    title={`Linked eWarranty: ${serverInv.linked_warranty_id}`}
                    data-testid="gst-warranty-badge"
                  >
                    <ShieldCheck size={14} />
                    {serverInv.linked_warranty_id}
                  </span>
                ) : (
                  <button
                    onClick={() => setShowWarrantyModal(true)}
                    disabled={!form.customer_id}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-violet-100 hover:bg-violet-200 border border-violet-300 text-violet-700 disabled:opacity-50 px-2.5 sm:px-3 py-2 rounded-lg"
                    data-testid="gst-create-warranty-btn"
                    title={form.customer_id ? 'Create an eWarranty from this invoice' : 'Save with a linked customer first'}
                  >
                    <ShieldCheck size={14} />
                    <span className="hidden sm:inline">Warranty</span>
                  </button>
                )}
                <ActionMenu testid="gst-more">
                  <ActionMenu.Item icon={<Download size={14} />} onClick={downloadPdf} testid="gst-more-download">
                    Download PDF
                  </ActionMenu.Item>
                  <ActionMenu.Item
                    icon={emailing ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />}
                    onClick={emailToCustomer}
                    disabled={emailing}
                    testid="gst-more-email"
                  >
                    {emailing ? 'Sending…' : 'Email to customer'}
                  </ActionMenu.Item>
                  {form.status === 'draft' && (
                    <ActionMenu.Item
                      icon={marking ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                      onClick={markSent}
                      disabled={marking}
                      tone="warning"
                      testid="gst-more-mark-sent"
                    >
                      Mark as Issued
                    </ActionMenu.Item>
                  )}
                  <ActionMenu.Item
                    icon={cloning ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
                    onClick={cloneInvoice}
                    disabled={cloning}
                    testid="gst-more-clone"
                  >
                    Clone invoice
                  </ActionMenu.Item>
                </ActionMenu>
              </>
            )}
            <select value={form.status} onChange={(e) => set('status', e.target.value)} className={`${inputCls} max-w-[130px]`} data-testid="gst-status">
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="partially_paid">Part paid</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button onClick={save} disabled={saving || !form.customer_snapshot.name} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 sm:px-4 py-2 rounded-lg" data-testid="gst-save-btn">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5">
        {err && (
          <div ref={errBannerRef} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-start justify-between gap-2" role="alert" aria-live="assertive" data-testid="gst-form-error">
            <span className="flex-1">{err}</span>
            <button type="button" onClick={clearError} className="text-red-500 hover:text-red-700 shrink-0" aria-label="Dismiss error">✕</button>
          </div>
        )}
        {!isNew && serverInv?.source_estimate_id && (
          <div className="bg-violet-50 border border-violet-200 text-violet-800 rounded px-3 py-2 text-sm flex items-center justify-between" data-testid="gst-source-est-banner">
            <span>Created from estimate <b>{serverInv.source_estimate_number || serverInv.source_estimate_id}</b>.</span>
            <button onClick={() => navigate(`/admin/billing/estimates/${serverInv.source_estimate_id}`)} className="underline font-medium">
              Open estimate →
            </button>
          </div>
        )}
        {!isNew && serverInv?.source_invoice_id && (
          <div className="bg-slate-50 border border-slate-200 text-slate-700 rounded px-3 py-2 text-sm">
            Cloned from GST invoice <b>{serverInv.source_invoice_number || serverInv.source_invoice_id}</b>.
          </div>
        )}

        {/* Bill To + Meta panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bill To</div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Existing customer (optional)</label>
                <div className="flex items-center gap-2">
                  <select value={form.customer_id} onChange={(e) => pickCustomer(e.target.value)} className={`${inputCls} flex-1`} data-testid="gst-customer-picker">
                    <option value="">— New / one-off —</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` · ${c.gstin}` : ''}</option>)}
                  </select>
                  {form.customer_id && (
                    <button
                      type="button"
                      onClick={detachCustomer}
                      className="text-[11px] font-semibold text-slate-500 hover:text-rose-600 whitespace-nowrap px-2 py-1 border border-slate-300 rounded hover:border-rose-300"
                      data-testid="gst-cust-detach"
                      title="Detach from customer master so you can freely edit the Bill-To fields below"
                    >Detach</button>
                  )}
                </div>
                {form.customer_id && (
                  <p className="text-[10px] text-slate-400 mt-1">
                    Editing name / phone / GSTIN below will auto-detach this invoice from the customer master.
                  </p>
                )}
              </div>
              {(vehicleOptions.length > 0 || form.vehicle_snapshot) && (
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">Vehicle (prints on the invoice)</label>
                  {vehicleOptions.length > 0 ? (
                    <select
                      value={form.vehicle_snapshot?.id || ''}
                      onChange={(e) => pickVehicle(e.target.value)}
                      className={inputCls}
                      data-testid="gst-vehicle-picker"
                    >
                      <option value="">— No vehicle on invoice —</option>
                      {form.vehicle_snapshot?.id && !vehicleOptions.some((v) => v.id === form.vehicle_snapshot.id) && (
                        <option value={form.vehicle_snapshot.id}>{vehLabel(form.vehicle_snapshot)} (saved)</option>
                      )}
                      {vehicleOptions.map((v) => <option key={v.id} value={v.id}>{vehLabel(v)}</option>)}
                    </select>
                  ) : (
                    <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
                      <span data-testid="gst-vehicle-saved">{vehLabel(form.vehicle_snapshot || {})}</span>
                      <button type="button" onClick={() => set('vehicle_snapshot', null)} className="text-slate-400 hover:text-red-500 underline" data-testid="gst-vehicle-remove">remove</button>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input type="text" value={form.customer_snapshot.name} onChange={(e) => setSnapDetached({ name: e.target.value })} className={inputCls} placeholder="Customer / Trade name *" data-testid="gst-cust-name" />
                <input type="text" value={form.customer_snapshot.gstin || ''} onChange={(e) => setSnapDetached({ gstin: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} placeholder="GSTIN (15 char, optional for B2C)" maxLength={15} data-testid="gst-cust-gstin" />
                <input type="text" value={form.customer_snapshot.phone || ''} onChange={(e) => setSnapDetached({ phone: e.target.value })} className={inputCls} placeholder="Phone" data-testid="gst-cust-phone" />
                <input type="email" value={form.customer_snapshot.email || ''} onChange={(e) => setSnap({ email: e.target.value })} className={inputCls} placeholder="Email" data-testid="gst-cust-email" />
              </div>
              <textarea rows={2} value={form.customer_snapshot.address || ''} onChange={(e) => setSnap({ address: e.target.value })} className={inputCls} placeholder="Billing address" />
            </div>
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Invoice Meta</div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Place of Supply</label>
                <select value={form.place_of_supply} onChange={(e) => set('place_of_supply', e.target.value)} className={inputCls} data-testid="gst-pos">
                  {STATES.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">Invoice date</label>
                  <input type="date" value={form.invoice_date} onChange={(e) => set('invoice_date', e.target.value)} className={inputCls} data-testid="gst-date" />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">Due date</label>
                  <input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} data-testid="gst-due-date" />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={form.reverse_charge} onChange={(e) => set('reverse_charge', e.target.checked)} data-testid="gst-reverse-charge" />
                Reverse Charge applies
              </label>
              <input type="text" value={form.subject} onChange={(e) => set('subject', e.target.value)} className={inputCls} placeholder="Subject (optional)" data-testid="gst-subject" />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Line Items</h2>
              <span className="text-[10px] text-slate-400 hidden sm:inline">HSN auto-fills from description on blur</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${preview.intra ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {preview.intra ? 'Intra-state · CGST + SGST' : 'Inter-state · IGST'}
              </span>
              <PartPicker onPick={addPart} testid="gst-add-part-btn" />
              <button onClick={addRow} className="inline-flex items-center gap-1.5 text-xs bg-teal-50 hover:bg-teal-100 border border-teal-200 text-teal-700 px-3 py-1.5 rounded-lg" data-testid="gst-add-item-btn">
                <Plus size={13} /> Add row
              </button>
            </div>
          </div>
          <WinbackPanel customerId={form.customer_id} applied={form.winback_code} onApply={applyWinback} />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-2 py-2 w-8">#</th>
                  <th className="text-left px-2 py-2">Description</th>
                  <th className="text-left px-2 py-2 w-32">HSN/SAC</th>
                  <th className="text-right px-2 py-2 w-16">Qty</th>
                  <th className="text-left px-2 py-2 w-24">Unit</th>
                  <th className="text-right px-2 py-2 w-28">Rate</th>
                  <th className="text-right px-2 py-2 w-16">Disc %</th>
                  <th className="text-right px-2 py-2 w-20">GST %</th>
                  <th className="text-right px-2 py-2 w-36">Total (incl. GST)</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => {
                  const line = computeLine(it, preview.intra);
                  const hasTarget = it.line_total_target !== '' && it.line_total_target != null;
                  const qtyN = Number(it.qty) || 0;
                  return (
                    <tr key={it.id} className="border-t border-slate-200 align-top" data-testid={`gst-item-${idx}`}>
                      <td className="px-2 py-2 text-slate-500">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <AutoGrowTextarea
                          minRows={2}
                          value={it.description}
                          onChange={(e) => setItem(idx, { description: e.target.value })}
                          onBlur={() => tryAutoMatchHsn(idx)}
                          className={`${inputCls} resize-y leading-snug overflow-hidden`}
                          placeholder="Service / product description (multi-line OK)"
                          data-testid={`gst-item-desc-${idx}`}
                        />
                        <PartChip item={it} index={idx} onUnlink={() => setItem(idx, { part_id: '', _stock: null })} />
                        <div className="mt-1">
                          <select
                            value={it.revenue_head || guessHead(it.description) || ''}
                            onChange={(e) => setItem(idx, { revenue_head: e.target.value })}
                            className="w-full text-[11px] bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-slate-700"
                            data-testid={`gst-item-head-${idx}`}
                            title="Revenue head — used in the monthly reports breakdown"
                          >
                            <option value="">— Revenue head (auto-suggested) —</option>
                            {REVENUE_HEADS.map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input list={`hsn-list-${idx}`} value={it.hsn_code} onChange={(e) => pickHsn(idx, e.target.value)} className={`${inputCls} font-mono`} placeholder="HSN/SAC" data-testid={`gst-item-hsn-${idx}`} />
                        <datalist id={`hsn-list-${idx}`}>
                          {hsnList.map((h) => <option key={`${h.id}`} value={h.code}>{h.type} {h.code} — {h.description} ({h.default_gst_rate}%)</option>)}
                        </datalist>
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" min="0" step="0.01" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className={`${inputCls} text-right`} data-testid={`gst-item-qty-${idx}`} />
                      </td>
                      <td className="px-2 py-2">
                        <select value={it.unit} onChange={(e) => setItem(idx, { unit: e.target.value })} className={inputCls} data-testid={`gst-item-unit-${idx}`}>
                          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number" min="0" step="0.01"
                          value={hasTarget ? (qtyN ? r2(line.gross / qtyN) : line.gross) : it.rate}
                          onChange={(e) => setItem(idx, { rate: e.target.value })}
                          disabled={hasTarget}
                          className={`${inputCls} text-right font-mono ${hasTarget ? 'bg-slate-100 text-slate-500' : ''}`}
                          placeholder="0.00"
                          title={hasTarget ? 'Auto-calculated from the locked line total' : undefined}
                          data-testid={`gst-item-rate-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" min="0" step="0.01" value={it.discount_pct} onChange={(e) => setItem(idx, { discount_pct: e.target.value })} className={`${inputCls} text-right`} placeholder="0" data-testid={`gst-item-disc-${idx}`} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" min="0" step="0.01" value={it.gst_rate} onChange={(e) => setItem(idx, { gst_rate: e.target.value })} className={`${inputCls} text-right`} placeholder="18" data-testid={`gst-item-gst-${idx}`} />
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <LineTotalInput
                            target={it.line_total_target}
                            computed={line.total}
                            onChange={(v) => setItem(idx, { line_total_target: v })}
                            className={`${inputCls} text-right font-mono font-bold ${hasTarget ? 'border-teal-400 bg-teal-50 text-teal-800' : 'text-teal-700'}`}
                            title="Line total incl. GST — type a round figure and the rate back-calculates exactly"
                            data-testid={`gst-item-total-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => line.total > 0 && setItem(idx, { line_total_target: String(Math.round(line.total)) })}
                            className="p-1 rounded hover:bg-teal-100 text-teal-600 shrink-0"
                            title="Round off to nearest ₹"
                            data-testid={`gst-item-round-${idx}`}
                          >
                            <Wand2 size={13} />
                          </button>
                        </div>
                        {hasTarget && (
                          <button
                            type="button"
                            onClick={() => setItem(idx, { line_total_target: '' })}
                            className="mt-1 text-[10px] text-slate-400 hover:text-red-500 underline"
                            data-testid={`gst-item-total-clear-${idx}`}
                          >
                            clear — rate drives total
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {form.items.length > 1 && (
                          <button onClick={() => delRow(idx)} className="p-1 rounded hover:bg-red-100 text-red-600" title="Remove row" data-testid={`gst-del-item-${idx}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* E-Way Bill (collapsible) */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowEwb((v) => !v)}
            className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-slate-50"
            data-testid="gst-ewb-toggle"
          >
            <div className="flex items-center gap-2">
              {showEwb ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Truck size={14} className="text-amber-600" />
              <span className="text-sm font-semibold text-slate-900">E-Way Bill</span>
              {(form.eway_bill?.ewb_no || form.eway_bill?.vehicle_no) && (
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">added</span>
              )}
            </div>
            <span className="text-xs text-slate-500">Required if invoice value &gt; ₹50,000</span>
          </button>
          {showEwb && (
            <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <input type="text" placeholder="EWB Number" value={form.eway_bill.ewb_no} onChange={(e) => set('eway_bill', { ...form.eway_bill, ewb_no: e.target.value })} className={`${inputCls} font-mono`} data-testid="gst-ewb-no" />
                <input type="date" placeholder="EWB Date" value={form.eway_bill.ewb_date || ''} onChange={(e) => set('eway_bill', { ...form.eway_bill, ewb_date: e.target.value })} className={inputCls} />
                <input type="text" placeholder="Vehicle No." value={form.eway_bill.vehicle_no} onChange={(e) => set('eway_bill', { ...form.eway_bill, vehicle_no: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} />
                <input type="text" placeholder="Transporter Name" value={form.eway_bill.transporter_name} onChange={(e) => set('eway_bill', { ...form.eway_bill, transporter_name: e.target.value })} className={inputCls} />
                <input type="text" placeholder="Transporter GSTIN/ID" value={form.eway_bill.transporter_id} onChange={(e) => set('eway_bill', { ...form.eway_bill, transporter_id: e.target.value })} className={`${inputCls} font-mono`} />
                <input type="number" placeholder="Distance (km)" value={form.eway_bill.distance_km} onChange={(e) => set('eway_bill', { ...form.eway_bill, distance_km: Number(e.target.value || 0) })} className={`${inputCls} text-right`} />
              </div>
            </div>
          )}
        </div>

        {/* Bank & Terms overrides (this document only) — mirrors non-GST form */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowOverrides((s) => !s)}
            className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-left hover:bg-slate-50"
            data-testid="gst-toggle-overrides"
          >
            <div className="flex items-center gap-2">
              {showOverrides ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="text-sm font-semibold text-slate-900">Bank &amp; Terms (this invoice only)</span>
              {OVERRIDE_FIELDS.some((f) => form[f] !== null && form[f] !== undefined) && (
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">customized</span>
              )}
            </div>
            <span className="text-xs text-slate-500">Defaults pulled from Billing Setup</span>
          </button>
          {showOverrides && (
            <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                Edits here only change this invoice&apos;s PDF. To change defaults for every document, edit <b>Billing Setup</b>.
              </p>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Bank details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ['bank_holder_name', 'Account holder name'],
                    ['bank_name', 'Bank name'],
                    ['bank_account_number', 'Account number'],
                    ['bank_ifsc', 'IFSC code'],
                    ['upi', 'UPI / GPay / PhonePe'],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-[11px] text-slate-500 mb-1">{label}</label>
                      <div className="relative">
                        <input
                          value={effective(k)}
                          onChange={(e) => setOverride(k, e.target.value)}
                          className={`${inputCls} ${form[k] !== null && form[k] !== undefined ? 'pr-9 border-amber-400' : ''}`}
                          placeholder={settingsFallback(k) || ''}
                          data-testid={`gst-override-${k}`}
                        />
                        {form[k] !== null && form[k] !== undefined && (
                          <button
                            type="button"
                            onClick={() => resetOverride(k)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                            title="Reset to default"
                            data-testid={`gst-reset-${k}`}
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {[
                ['terms_pay', 'Payment terms'],
                ['terms_service', 'Service terms & conditions'],
              ].map(([k, label]) => {
                const overridden = form[k] !== null && form[k] !== undefined;
                const text = overridden ? arrayToLines(form[k]) : arrayToLines(billingSettings?.[k]);
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</label>
                      {overridden && (
                        <button
                          type="button"
                          onClick={() => resetOverride(k)}
                          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
                          data-testid={`gst-reset-${k}`}
                        >
                          <RotateCcw size={11} /> Reset to default
                        </button>
                      )}
                    </div>
                    <textarea
                      rows={Math.min(10, Math.max(3, (text.match(/\n/g)?.length || 0) + 2))}
                      value={text}
                      onChange={(e) => setOverride(k, linesToArray(e.target.value))}
                      className={`${inputCls} ${overridden ? 'border-amber-400' : ''}`}
                      placeholder="One line per term…"
                      data-testid={`gst-override-${k}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Notes + Worked-on-by + Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Notes</h2>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Payment terms, warranty period, etc. (printed on invoice)"
              className={inputCls}
              data-testid="gst-notes"
            />
            <div className="mt-4 pt-4 border-t border-slate-100">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Worked on by (Incentive credit)</h3>
              {staffList.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No active staff. Add staff in <a href="/admin/staff" className="text-teal-700 underline">Staff &amp; Attendance</a> first.</p>
              ) : (
                <>
                  <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
                    {staffList.map((s) => {
                      const checked = (form.worked_on_by_ids || []).includes(s.id);
                      return (
                        <label key={s.id} className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-white rounded cursor-pointer text-sm">
                          <span className="flex items-center gap-2">
                            <input type="checkbox" checked={checked} onChange={() => {
                              const cur = form.worked_on_by_ids || [];
                              set('worked_on_by_ids', checked ? cur.filter((x) => x !== s.id) : [...cur, s.id]);
                            }} data-testid={`gst-workedby-${s.id}`} />
                            <span>{s.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.level === 'senior' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>{s.level}</span>
                          </span>
                          <span className="text-[11px] text-slate-500 font-mono">₹{s.incentive_per_vehicle}/veh</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <label className="text-slate-600">Override rate (₹ per staff):</label>
                    <input type="number" min={0} value={form.incentive_per_staff_override ?? ''} onChange={(e) => set('incentive_per_staff_override', e.target.value)} placeholder="Default" className={`${inputCls} max-w-[120px]`} data-testid="gst-incentive-override" />
                    <span className="text-[11px] text-slate-400">Blank = each staff&apos;s own rate</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Summary (live preview)</h2>
            <div className="space-y-1.5 text-sm">
              {[
                ['Sub Total', preview.sub_total],
                ['Discount', -preview.total_discount],
                ['Taxable Value', preview.taxable_value],
                ...(preview.intra
                  ? [['CGST', preview.cgst_total], ['SGST', preview.sgst_total]]
                  : [['IGST', preview.igst_total]]),
                ['Total Tax', preview.total_tax],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between text-slate-600">
                  <span>{k}</span>
                  <span className="font-mono">{fmtINR(v)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between items-center">
                <span className="text-base font-bold text-slate-900">Grand Total</span>
                <span className="font-mono font-bold text-2xl text-teal-700" data-testid="gst-grand-total">₹ {fmtINR(preview.grand_total)}</span>
              </div>
              {/* Advance received — entered by staff; the balance due below
                  auto-recomputes and is what the PDF prints as the final
                  amount payable. Blank / 0 hides the balance-due row. */}
              <div className="mt-3 flex justify-between items-center gap-3">
                <label className="text-sm text-slate-700 whitespace-nowrap" htmlFor="gst-advance-received">
                  Advance received <span className="text-[10px] text-slate-400">(incl. GST)</span>
                </label>
                <input
                  id="gst-advance-received"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.advance_received || ''}
                  onChange={(e) => set('advance_received', e.target.value)}
                  placeholder="0.00"
                  className={`${inputCls} max-w-[140px] text-right font-mono`}
                  data-testid="gst-advance-received"
                />
              </div>
              {Number(form.advance_received || 0) > 0 && (
                <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between items-center">
                  <span className="text-base font-bold text-rose-700">Balance Due</span>
                  <span
                    className="font-mono font-bold text-2xl text-rose-700"
                    data-testid="gst-balance-due"
                  >₹ {fmtINR(Math.max(0, Number(preview.grand_total || 0) - Number(form.advance_received || 0)))}</span>
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              Server re-computes tax on save — this is a live preview only.
            </p>
          </div>
          {!isNew && serverInv && (
            <InvoicePaymentLinkCard
              invoiceId={id}
              serverInv={serverInv}
              authHeader={authHeader}
              onCreated={loadInvoice}
            />
          )}
          {!isNew && serverInv && <InvoiceNotes invoice={serverInv} canEdit onChanged={loadInvoice} />}
        </div>
      </main>

      {/* Record Payment modal — reuses the non-GST component with apiBase */}
      {showPayModal && serverInv && (
        <RecordPaymentModal
          invoice={serverInv}
          apiBase={`${API}/gst/invoices`}
          onClose={() => setShowPayModal(false)}
          onSendThanks={(inv, payment) => sendPaymentThanks({ invoice: inv, payment, gst: true, customer: form.customer_snapshot, authHeader }).catch(notifyErr)}
          onSaved={(updated) => {
            setServerInv(updated);
            setForm((f) => ({ ...f, amount_paid: updated.amount_paid, status: updated.status }));
          }}
        />
      )}

      {/* eWarranty modal — reuses the non-GST component with apiBase */}
      {showWarrantyModal && serverInv && form.customer_id && (
        <InvoiceWarrantyModal
          invoice={serverInv}
          customer={customers.find((c) => c.id === form.customer_id) || {}}
          apiBase={`${API}/gst/invoices`}
          onClose={() => setShowWarrantyModal(false)}
          onCreated={(w) => {
            setServerInv((s) => ({ ...s, linked_warranty_id: w.warranty_id }));
          }}
        />
      )}
    </div>
  );
};

export default GstInvoiceForm;
